/**
 * Utility for formatting relative dates (e.g., "Today", "In 5 days", "2d ago")
 */

export interface RelativeDateResult {
  /** Relative text (e.g., "Today", "In 5 days", "2d ago") */
  relative: string;
  /** Formatted absolute date (e.g., "Dec 25") */
  absolute: string;
  /** Whether the date is in the past */
  isPast: boolean;
  /** Number of days difference (negative for past, positive for future) */
  diffDays: number;
}

export interface RelativeDateOptions {
  /** Timezone for formatting (default: UTC) */
  timezone?: string;
  /** Show short format like "5d" instead of "In 5 days" (default: false) */
  shortFormat?: boolean;
}

/**
 * Format a date relative to now (e.g., "Today", "Tomorrow", "In 5 days", "2d ago")
 * @param date - The date to format
 * @param options - Formatting options
 * @returns Object with relative text, absolute date, and metadata
 */
export function formatRelativeDate(
  date: Date | string,
  options: RelativeDateOptions = {}
): RelativeDateResult {
  const { timezone = 'UTC', shortFormat = false } = options;

  const targetDate = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();

  // Get calendar dates in the specified timezone for proper day comparison
  // This ensures we compare "Jan 29" vs "Jan 30" in the org timezone, not raw milliseconds
  const targetDateStr = targetDate.toLocaleDateString('en-CA', { timeZone: timezone }); // YYYY-MM-DD format
  const nowDateStr = now.toLocaleDateString('en-CA', { timeZone: timezone }); // YYYY-MM-DD format

  // Parse the date strings to compare calendar days
  const [targetYear, targetMonth, targetDay] = targetDateStr.split('-').map(Number);
  const [nowYear, nowMonth, nowDay] = nowDateStr.split('-').map(Number);

  // Create date objects for midnight on each day (for day difference calculation)
  const targetMidnight = new Date(targetYear, targetMonth - 1, targetDay);
  const nowMidnight = new Date(nowYear, nowMonth - 1, nowDay);

  // Calculate difference in calendar days
  const diffTime = targetMidnight.getTime() - nowMidnight.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
  const isPast = diffDays < 0;
  const absDays = Math.abs(diffDays);

  // Format absolute date
  const absolute = targetDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: timezone,
  });

  // Format relative text
  let relative: string;

  if (diffDays === 0) {
    relative = 'Today';
  } else if (diffDays === 1) {
    relative = 'Tomorrow';
  } else if (diffDays === -1) {
    relative = 'Yesterday';
  } else if (isPast) {
    relative = shortFormat ? `${absDays}d ago` : `${absDays} days ago`;
  } else if (diffDays <= 7) {
    relative = shortFormat ? `In ${diffDays}d` : `In ${diffDays} days`;
  } else {
    // More than 7 days - just show the date
    relative = absolute;
  }

  return {
    relative,
    absolute,
    isPast,
    diffDays,
  };
}

/**
 * Get just the relative date string
 * @param date - The date to format
 * @param options - Formatting options
 * @returns Relative date string (e.g., "Today", "In 5 days")
 */
export function getRelativeDateString(
  date: Date | string,
  options: RelativeDateOptions = {}
): string {
  return formatRelativeDate(date, options).relative;
}
